import React, { useEffect, useState } from 'react';
import { Popover } from 'antd';
import styled from 'styled-components';
import { IsMobile } from 'Utils/UtilityFunctions';

import UpAngleIcon from '../../common/assets/svgIcons/ArrowUpIcon';
import DownAngleIcon from '../../common/assets/svgIcons/DownAngleIcon';
import { greyColor, primaryColor, primaryFont, wMedium } from '../../../Utils/utilsStyle';

type ButtonType = 'text' | 'iconText' | 'iconTextDropdown' | 'suffix';

const ButtonWrapper = styled.div<{ $active: boolean, isSuffix?: boolean, variant: ButtonType, width?: string }>`
  height: 30px;
  width: ${({ width }) => width || '112px'};
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? primaryColor : '#DADADA')};
  background: #ffffff;
  transition: all 0.2s ease;
  justify-content: ${({ isSuffix, variant }) => (isSuffix && variant === 'suffix') ? 'center' : 'space-between'};
`;

const DropdownIconWrapper = styled.div<{ $active: boolean }>`
  svg {
    width: 10px;
    height: 7px;
  }
`

const ButtonText = styled.span<{ $active: boolean }>`
  display: inline-flex;
  color: ${({ $active }) => ($active ? primaryColor : greyColor)};
  font-family: ${primaryFont};
  font-size: 14px;
  font-weight: ${wMedium};
  letter-spacing: -0.4px;
  user-select: none;
`;

const IconWrapper = styled.span<{ $active: boolean; $isMobile: boolean }>`
  display: flex;
  align-items: center;
  svg {
    margin-right: ${({ $isMobile }) => ($isMobile ? '0px' : '5px')};
    g {
      fill: ${({ $active }) => ($active ? primaryColor : greyColor)};
    }
    path {
      fill: ${({ $active }) => ($active ? primaryColor : greyColor)};
    }
  }
`;

const Separator = styled.div<{ $active: boolean }>`
  width: 1px;
  height: 18px;
  background-color: ${({ $active }) => $active ? primaryColor : '#DDD'};
  margin: 0 7px 0 10px;
`;

interface PopoverButtonProps {
  isActive?: boolean;
  open: boolean;
  setOpen: (val: boolean) => void;
  variant?: ButtonType;
  isSuffix?: boolean;
  text: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  title?: React.ReactNode;
  width?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';
}

const PopoverButton: React.FC<PopoverButtonProps> = ({
  open,
  setOpen,
  isActive = false,
  variant = 'text',
  isSuffix = false,
  text,
  icon,
  content,
  title,
  width,
  placement,
}) => {
  const isMobile = IsMobile();
  useEffect(() => {
    const handleScroll = () => {
      setOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);
  return (
    <Popover
      content={content}
      title={title}
      trigger="click"
      arrow={false}
      open={open}
      onOpenChange={setOpen}
      getPopupContainer={(triggerNode) => { return triggerNode.parentElement || document.body; }}
      placement={placement}
      className='ant-custom-popover'
    >
      <ButtonWrapper $active={open || isActive} isSuffix={isSuffix} variant={variant} width={width}>

        <ButtonText $active={open || isActive}>
          {isSuffix && (
            <IconWrapper $active={open || isActive} $isMobile={isMobile}>{icon}</IconWrapper>
          )}
          {text}
        </ButtonText>

        {variant === 'iconTextDropdown' && (
          <DropdownIconWrapper $active={open || isActive} className='d-flex align-items-center'>
            <Separator $active={open || isActive} />
            {open ? <UpAngleIcon /> : <DownAngleIcon color={isActive ? primaryColor : greyColor} />}
          </DropdownIconWrapper>
        )}
      </ButtonWrapper>
    </Popover>
  );
};

export default PopoverButton;
